import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import RaisedButton from 'material-ui/RaisedButton';
import styled from 'styled-components';
import api from 'services';
import { showNotification } from 'helpers';

/**
 * @typedef {object} SessionsLimitPayload
 * @property {number} total
 * @property {number} web
 * @property {number} mobile
 * @property {number} android
 * @property {number} iphone
 */

const DEFAULT_SESSION_LIMITS = {
    total: -1,
    web: -1,
    mobile: -1,
    android: -1,
    iphone: -1,
};

/** Roles the API rejects for session-limit updates */
const ROLES_WITHOUT_EDITABLE_SESSION_LIMITS = new Set(['admin', 'moderator']);

const segmentBorder = '#bdc3c7';
const pageText = '#34495e';
const segmentActiveBg = '#ecf0f1';

function mergeSessionLimits(source = {}) {
    return {
        ...DEFAULT_SESSION_LIMITS,
        ...source,
    };
}

/** @param {unknown} role */
function normalizeUserRole(role) {
    return typeof role === 'string' ? role.trim().toLowerCase() : '';
}

/**
 * dedupe strings
 * @param {string[]} items
 */
function dedupeStrings(items) {
    return [...new Set(items)];
}

/**
 * collect session limit warnings
 * @param {SessionsLimitPayload} v
 * @returns {string[]}
 */
function collectSessionLimitWarnings(v) {
    const messages = [];
    const {
        total, web, mobile, android, iphone,
    } = v;

    const mobileIsFinitePool = mobile !== 0 && mobile !== -1;
    const bothNativeBlocked = android === 0 && iphone === 0;

    if (bothNativeBlocked && mobileIsFinitePool) {
        messages.push('With Android and iPhone both set to 0, no mobile sessions are allowed. '
            + 'The mobile limit does nothing useful until at least one native platform allows sessions (−1 unlimited or a positive cap).');
    }

    if (
        android === 0
        && iphone !== 0
        && iphone !== -1
        && (mobile === -1 || mobile > 1)
    ) {
        messages.push('Android is not allowed (0). Only iPhone sessions can contribute to the mobile pool.');
    }

    if (iphone === 0 && android !== 0 && android !== -1) {
        messages.push('iPhone is not allowed (0). Only Android sessions can contribute to the mobile pool.');
    }

    if (total !== -1) {
        /** @type {Array<[string, number]>} */
        const cappedDimensions = [
            ['web', web],
            ['mobile pool', mobile],
            ['Android', android],
            ['iPhone', iphone],
        ];
        let totalStricter = false;
        for (let di = 0; di < cappedDimensions.length; di += 1) {
            const cap = cappedDimensions[di][1];
            if (cap !== -1 && cap > 0 && total < cap) {
                totalStricter = true;
                break;
            }
        }
        if (totalStricter) {
            const totalMsg = `Overall total (${total}) is stricter than at least one other limit you configured. `
                + 'Concurrent sessions cannot exceed total, even where another cap is higher.';
            messages.push(totalMsg);
        }
    }

    if (
        mobile !== -1
        && android !== -1
        && android > 0
        && mobile > android
        && iphone === -1
    ) {
        messages.push('Mobile is higher than your Android cap; in practice enforcement usually stops at the Android limit.');
    }

    if (
        mobile !== -1
        && iphone !== -1
        && iphone > 0
        && mobile > iphone
        && android === -1
    ) {
        messages.push('Mobile is higher than your iPhone cap; in practice enforcement usually stops at the iPhone limit.');
    }

    const allThreeFiniteMobileStack = mobile !== -1 && android !== -1 && iphone !== -1;

    if (allThreeFiniteMobileStack && !bothNativeBlocked && mobile > android + iphone) {
        messages.push('Mobile cap exceeds Android and iPhone combined. Per-platform caps will usually bite before the shared mobile ceiling.');
    }

    return dedupeStrings(messages);
}

const SegmentGroup = styled.div`
    display: inline-flex;
    border: 1px solid ${segmentBorder};
    border-radius: 2px;
    overflow: hidden;
`;

const SegmentButton = styled.button.attrs({ type: 'button' })`
    padding: 5px 12px;
    font-size: 12px;
    line-height: 1.2;
    margin: 0;
    cursor: pointer;
    font-family: inherit;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    appearance: none;
    -webkit-appearance: none;
    border: none;
    font-weight: 400;
    background: #fff;
    color: ${pageText};

    &.segment_btn_active {
        font-weight: 600;
        background: ${segmentActiveBg};
    }

    &.segment_btn_divider {
        border-left: 1px solid ${segmentBorder};
    }
`;

const LimitValueInput = styled.input`
    width: 56px;
    margin-left: 12px;
    padding: 4px;
    font-size: inherit;
    line-height: 1.2;
    text-align: center;
    border: 1px solid ${segmentBorder};
    border-radius: 2px;
    background-color: #fff;
    color: ${pageText};
    outline: none;
    vertical-align: middle;
`;

const WarningsBanner = styled.div`
    margin: 10px 12px 6px;
    padding: 8px 12px;
    font-size: 12px;
    line-height: 1.45;
    color: #664d03;
    background: #fff3cd;
    border: 1px solid ${segmentBorder};
    border-radius: 4px;
`;

const WarningList = styled.ul`
    margin: 6px 0 0;
    padding-left: 18px;
`;

const OrderHelp = styled.div`
    margin: 8px 12px 0;
    font-size: 11px;
    color: #757575;
    line-height: 1.45;
`;

/**
 * @param {object} props
 * @param {string} props.label
 * @param {number} props.value
 * @param {(v: number) => void} props.onChange
 * @param {boolean} [props.nested]
 */
const LimitInput = ({
    label,
    value,
    onChange,
    nested,
}) => {
    const isUnlimited = value === -1;

    const onNumberInputChange = (e) => {
        const raw = e.target.value;
        if (raw === '') {
            onChange(0);
            return;
        }
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0) {
            onChange(n);
        }
    };

    const rowClass = nested
        ? 'col-md-offset-1 col-md-11 payment-history_entry'
        : 'col-md-12 payment-history_entry';

    return (
        <div className="row">
            <div className={rowClass}>
                <div className="row">
                    <div className="col-md-4 bold">
                        {label}
                    </div>
                    <div className="col-md-8">
                        <SegmentGroup>
                            <SegmentButton
                                className={classNames({ segment_btn_active: isUnlimited })}
                                onClick={() => onChange(-1)}
                            >
                                Unlimited
                            </SegmentButton>
                            <SegmentButton
                                className={classNames(
                                    'segment_btn_divider',
                                    { segment_btn_active: !isUnlimited },
                                )}
                                onClick={() => {
                                    if (isUnlimited) {
                                        onChange(1);
                                    }
                                }}
                            >
                                Limited
                            </SegmentButton>
                        </SegmentGroup>
                        {!isUnlimited && (
                            <LimitValueInput
                                type="number"
                                min={0}
                                value={String(value)}
                                onChange={onNumberInputChange}
                                aria-label={`${label} limit`}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * @param {object} root0
 * @param {string} root0.name
 * @param {string} root0.label
 * @param {*} root0.control
 * @param {boolean} [root0.nested]
 */
const LimitFieldController = ({
    name,
    label,
    control,
    nested,
}) => (
    <Controller
        name={name}
        control={control}
        render={({ onChange, value }) => (
            <LimitInput label={label} value={value} onChange={onChange} nested={nested} />
        )}
    />
);

const SectionTitleRow = ({ children }) => (
    <div className="row">
        <div className="col-md-12 m-t-1 payment-history_header">
            <div className="row">
                <div className="col-md-12 bold">
                    {children}
                </div>
            </div>
        </div>
    </div>
);

/** @param {number | undefined | null} v */
function coerceLimitField(v, fallback) {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

const SessionLimits = ({
    data,
    fetchUser,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
    } = useForm({
        defaultValues: mergeSessionLimits(data.sessionsLimit),
        mode: 'onChange',
    });

    const total = watch('total');
    const web = watch('web');
    const mobile = watch('mobile');
    const android = watch('android');
    const iphone = watch('iphone');

    const warnings = useMemo(
        () =>
            collectSessionLimitWarnings({
                total: coerceLimitField(total, DEFAULT_SESSION_LIMITS.total),
                web: coerceLimitField(web, DEFAULT_SESSION_LIMITS.web),
                mobile: coerceLimitField(mobile, DEFAULT_SESSION_LIMITS.mobile),
                android: coerceLimitField(android, DEFAULT_SESSION_LIMITS.android),
                iphone: coerceLimitField(iphone, DEFAULT_SESSION_LIMITS.iphone),
            }),
        [total, web, mobile, android, iphone],
    );

    useEffect(() => {
        reset(mergeSessionLimits(data.sessionsLimit));
    }, [data._id, data.sessionsLimit, reset]);

    /**
     * @param {SessionsLimitPayload} sessionsLimit
     * @returns {Promise<unknown>}
     */
    const updateSessionLimitsRequest = sessionsLimit =>
        api.updateUserSessionLimit(data._id, sessionsLimit);

    const saveMutation = useMutation(updateSessionLimitsRequest, {
        onSuccess: () => {
            showNotification('Session limits updated successfully', 'success');
            if (fetchUser) {
                fetchUser();
            }
        },
        onError: (error) => {
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : 'Could not update session limits';
            showNotification(message, 'error');
        },
    });

    /** @param {SessionsLimitPayload} formValues */
    const onSubmit = (formValues) => {
        saveMutation.mutate(formValues);
    };

    const isSaving = saveMutation.isLoading;

    const cannotEditSessionLimits = ROLES_WITHOUT_EDITABLE_SESSION_LIMITS.has(normalizeUserRole(data.role));

    return (
        <div>
            <h4>SESSION LIMITS</h4>
            <div className="m-t-1">
                Manage the maximum number of concurrent sessions allowed for this user across different platforms.
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="m-t-1">
                    <div className="row">
                        <div className="col-md-12 m-t-2">
                            <div className="payment-history">
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="text-center bold">
                                            Platforms & limits
                                        </div>
                                    </div>
                                </div>

                                <SectionTitleRow>Account-wide</SectionTitleRow>
                                <LimitFieldController name="total" label="Total" control={control} />

                                <SectionTitleRow>By channel</SectionTitleRow>
                                <LimitFieldController name="web" label="Web" control={control} />
                                <LimitFieldController name="mobile" label="Mobile (all apps)" control={control} />

                                <SectionTitleRow>Native apps</SectionTitleRow>
                                <LimitFieldController name="android" label="Android" control={control} nested />
                                <LimitFieldController name="iphone" label="iPhone" control={control} nested />

                                {warnings.length > 0 ? (
                                    <WarningsBanner
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <div className="bold">Configuration notes (not enforced - only for information)</div>
                                        <WarningList>
                                            {
                                                warnings.map(msg => (
                                                    <li key={msg}>{msg}</li>
                                                ))
                                            }
                                        </WarningList>
                                    </WarningsBanner>
                                ) : null}
                                <OrderHelp>
                                    <span className="bold">How limits stack:</span>
                                    {' '}
                                    For mobile traffic we evaluate the native cap (Android or iPhone),
                                    then the shared mobile pool,
                                    then the account total. Web only uses web + total.
                                </OrderHelp>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="m-t-2">
                    <span
                        style={{ display: 'inline-block', verticalAlign: 'top' }}
                        title={
                            cannotEditSessionLimits
                                ? 'Administrators and moderators have no editable session limits.'
                                : undefined
                        }
                    >
                        <RaisedButton
                            type="submit"
                            label={isSaving ? 'Saving...' : 'Save Limits'}
                            primary
                            disabled={isSaving || !data._id || cannotEditSessionLimits}
                            style={cannotEditSessionLimits ? { opacity: 0.65 } : undefined}
                        />
                    </span>
                    {cannotEditSessionLimits ? (
                        <div style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: '#757575',
                            maxWidth: 360,
                        }}
                        >
                            Not available — admin/moderator accounts don’t use session limits.
                        </div>
                    ) : null}
                </div>
            </form>
        </div>
    );
};

export default SessionLimits;



// WEBPACK FOOTER //
// ./app/components/Users/Details/SessionLimits.jsx